import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { portalApi } from '@/api/portal'
import { X, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePortalDates } from '@/hooks/useDates'
import { guardPreview } from '@/lib/previewMode'

interface Props {
  onClose: () => void
  onSubmitted?: () => void
}

export default function BookingEnquiryModal({ onClose, onSubmitted }: Props) {
  const { formatDate } = usePortalDates()
  const queryClient = useQueryClient()

  const [courseId, setCourseId] = useState('')
  const [courseNameFreeText, setCourseNameFreeText] = useState('')
  const [preferredDate, setPreferredDate] = useState('')
  const [places, setPlaces] = useState(1)
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { data: courses = [] } = useQuery({
    queryKey: ['portal-courses'],
    queryFn: () => portalApi.getCourses().then(r => r.data),
  })

  const { data: staff = [] } = useQuery({
    queryKey: ['portal-staff'],
    queryFn: () => portalApi.getStaff().then(r => r.data),
  })

  const { data: termsStatus } = useQuery({
    queryKey: ['portal-terms-status'],
    queryFn: () => portalApi.getTermsStatus().then(r => r.data),
  })

  const activeStaff = staff
    .filter(s => s.isActive)
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))

  const toggleStaff = (id: string) => {
    setSelectedStaffIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      // Ensure attendee count is never less than selected staff count
      setPlaces(p => Math.max(p, next.size))
      return next
    })
  }

  const mutation = useMutation({
    mutationFn: () => portalApi.submitEnquiry({
      courseId: courseId || null,
      courseNameFreeText: !courseId ? courseNameFreeText.trim() || null : null,
      preferredDate: preferredDate || null,
      places,
      message: message.trim() || null,
      staffMemberIds: [...selectedStaffIds],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['portal-enquiries'] })
      setSubmitted(true)
      onSubmitted?.()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!courseId && !courseNameFreeText.trim()) return
    // Admins previewing the portal can fill the form in, but not send it.
    if (guardPreview()) return
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Request a booking</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-10 text-center space-y-3">
            <div className="text-4xl">✅</div>
            <p className="text-lg font-semibold text-gray-900">Enquiry sent!</p>
            <p className="text-sm text-gray-500">
              We've received your booking request and will be in touch shortly.
            </p>
            <Button className="mt-4" onClick={onClose}>Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Course */}
            <div className="space-y-1.5">
              <Label htmlFor="course">Course</Label>
              {courses.length > 0 ? (
                <select
                  id="course"
                  value={courseId}
                  onChange={e => setCourseId(e.target.value)}
                  className="w-full border border-gray-300 rounded-[var(--brand-radius-sm)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
                >
                  <option value="">— Select a course —</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.code ? ` (${c.code})` : ''}
                    </option>
                  ))}
                  <option value="__other">Other / not listed</option>
                </select>
              ) : (
                <Input
                  id="course"
                  placeholder="e.g. Manual Handling"
                  value={courseNameFreeText}
                  onChange={e => setCourseNameFreeText(e.target.value)}
                  required
                />
              )}
            </div>

            {/* Free-text course name when "Other" selected */}
            {courseId === '__other' && (
              <div className="space-y-1.5">
                <Label htmlFor="courseOther">Course name</Label>
                <Input
                  id="courseOther"
                  placeholder="Describe the course you need"
                  value={courseNameFreeText}
                  onChange={e => setCourseNameFreeText(e.target.value)}
                  required
                />
              </div>
            )}

            {/* Preferred date + attendees row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="date">Preferred date <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  id="date"
                  type="date"
                  value={preferredDate}
                  onChange={e => setPreferredDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="places">Number of attendees</Label>
                <Input
                  id="places"
                  type="number"
                  min={1}
                  max={100}
                  value={places === 0 ? '' : places}
                  onChange={e => { const v = e.target.value; setPlaces(v === '' ? 0 : parseInt(v) || 0) }}
                  onBlur={() => setPlaces(p => Math.min(100, Math.max(1, p)))}
                  required
                />
              </div>
            </div>

            {/* Staff selection */}
            {activeStaff.length > 0 && (
              <div className="space-y-1.5">
                <Label>
                  Staff to attend{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                  {selectedStaffIds.size > 0 && (
                    <span className="ml-2 text-[var(--brand-primary)] font-medium">
                      {selectedStaffIds.size} selected
                    </span>
                  )}
                </Label>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-44 overflow-y-auto">
                  {activeStaff.map(s => {
                    const checked = selectedStaffIds.has(s.id)
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStaff(s.id)}
                          className="w-4 h-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] cursor-pointer"
                        />
                        <span className="text-sm text-gray-800">
                          {s.firstName} {s.lastName}
                        </span>
                        {s.email && (
                          <span className="text-xs text-gray-400 ml-auto truncate max-w-32">
                            {s.email}
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Message */}
            <div className="space-y-1.5">
              <Label htmlFor="message">Additional notes <span className="text-gray-400 font-normal">(optional)</span></Label>
              <textarea
                id="message"
                rows={3}
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Any specific requirements or preferred times…"
                className="w-full border border-gray-300 rounded-[var(--brand-radius-sm)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] resize-none"
              />
            </div>

            {mutation.isError && (
              <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
            )}

            {termsStatus && termsStatus.termsVersion > 0 && termsStatus.acceptedAt && (
              <p className="text-xs text-gray-400">
                By submitting, you're bound by our Terms &amp; Conditions (v{termsStatus.termsVersion}, accepted{' '}
                {formatDate(termsStatus.acceptedAt, { day: 'numeric', month: 'short', year: 'numeric' })}).
              </p>
            )}

            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={mutation.isPending} className="flex-1 gap-2">
                <Send size={15} />
                {mutation.isPending ? 'Sending…' : 'Send enquiry'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
